package com.royceecoleman.bill.billing

import com.royceecoleman.bill.auth.authenticatedFetch
import com.royceecoleman.bill.shared.billing.BillingAccess
import com.royceecoleman.bill.shared.billing.BillingAudit
import com.royceecoleman.bill.shared.billing.BillingClient
import com.royceecoleman.bill.shared.billing.BillingDownload
import com.royceecoleman.bill.shared.billing.BillingExample
import com.royceecoleman.bill.shared.billing.BillingLink
import com.royceecoleman.bill.shared.billing.BillingProfile
import com.royceecoleman.bill.shared.billing.BillingRules
import com.royceecoleman.bill.shared.billing.BillingWorkspace
import com.royceecoleman.bill.shared.billing.BulkApproveRequest
import com.royceecoleman.bill.shared.billing.BulkApproveResult
import com.royceecoleman.bill.shared.billing.NoteImportRequest
import com.royceecoleman.bill.shared.billing.NoteImportResult
import com.royceecoleman.bill.shared.billing.NoteParsing
import com.royceecoleman.bill.shared.billing.PreviousBill
import com.royceecoleman.bill.shared.billing.PreviousBillUpload
import com.royceecoleman.bill.shared.billing.RedraftRequest
import com.royceecoleman.bill.shared.billing.RevisionRequest
import com.royceecoleman.bill.shared.billing.RulesChatRequest
import com.royceecoleman.bill.shared.billing.RulesEdit
import com.royceecoleman.bill.shared.billing.RulesRestoreRequest
import com.royceecoleman.bill.shared.billing.RulesUpdate
import com.royceecoleman.bill.shared.billing.RulesUpload
import com.royceecoleman.bill.shared.billing.WordingCheck
import com.royceecoleman.bill.shared.billing.WordingCheckSuggestions
import com.royceecoleman.bill.shared.billing.WordingChecks
import com.royceecoleman.bill.shared.billing.WordingChecksEdit
import com.royceecoleman.bill.shared.billing.SourceNote
import com.royceecoleman.bill.shared.billing.WorkspaceCreate
import com.royceecoleman.bill.shared.billing.WorkspaceEdit
import com.royceecoleman.bill.shared.billing.WorkspaceHome
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import org.w3c.dom.HTMLAnchorElement
import org.w3c.files.Blob
import org.w3c.files.BlobPropertyBag
import kotlin.js.unsafeCast

internal val workbenchJson =
    Json {
        ignoreUnknownKeys = true
        encodeDefaults = true
    }

internal suspend inline fun <reified T> workbenchCall(
    path: String = "",
    method: String = "GET",
    body: String? = null,
): T {
    val response = authenticatedFetch("/api/workbench$path", method, body, "application/json")
    val text = response.text().await()
    if (!response.ok) {
        if (response.status.toInt() == 401) window.location.href = "/login"
        val message =
            runCatching {
                workbenchJson
                    .parseToJsonElement(text)
                    .jsonObject["message"]
                    ?.jsonPrimitive
                    ?.content
            }.getOrNull()
        throw IllegalStateException(message ?: text.take(500).ifBlank { "Request failed. Please retry." })
    }
    return workbenchJson.decodeFromString(text)
}

internal suspend fun getWorkbench(): WorkspaceHome = workbenchCall()

internal suspend fun askBillingGuide(
    request: com.royceecoleman.bill.shared.billing.GuideRequest,
): com.royceecoleman.bill.shared.billing.GuideReply = workbenchCall("/guide", "POST", workbenchJson.encodeToString(request))

internal suspend fun getWorkspace(id: String): BillingWorkspace = workbenchCall("/workspaces/$id")

internal suspend fun getBillingRules(): BillingRules = workbenchCall("/rules")

internal suspend fun saveBillingRules(
    revision: Int,
    text: String,
): RulesUpdate = workbenchCall("/rules", "PUT", workbenchJson.encodeToString(RulesEdit(revision, text)))

internal suspend fun chatAboutRules(request: RulesChatRequest): RulesUpdate =
    workbenchCall("/rules/chat", "POST", workbenchJson.encodeToString(request))

internal suspend fun uploadBillingRules(upload: RulesUpload): RulesUpdate =
    workbenchCall("/rules/upload", "POST", workbenchJson.encodeToString(upload))

internal suspend fun undoRulesChange(revision: Int): RulesUpdate =
    workbenchCall("/rules/undo", "POST", workbenchJson.encodeToString(RevisionRequest(revision)))

internal suspend fun restoreRulesVersion(
    revision: Int,
    versionRevision: Int,
): RulesUpdate = workbenchCall("/rules/restore", "POST", workbenchJson.encodeToString(RulesRestoreRequest(revision, versionRevision)))

internal suspend fun getWordingChecks(): WordingChecks = workbenchCall("/rules/checks")

internal suspend fun saveWordingChecks(
    revision: Int,
    checks: List<WordingCheck>,
): WordingChecks = workbenchCall("/rules/checks", "PUT", workbenchJson.encodeToString(WordingChecksEdit(revision, checks)))

internal suspend fun suggestWordingChecks(): WordingCheckSuggestions = workbenchCall("/rules/checks/suggest", "POST", "{}")

internal suspend fun saveBillingProfile(profile: BillingProfile): BillingProfile =
    workbenchCall("/profiles", "POST", workbenchJson.encodeToString(profile))

internal suspend fun createWorkspace(request: WorkspaceCreate): BillingWorkspace =
    workbenchCall("/workspaces", "POST", workbenchJson.encodeToString(request))

internal suspend fun generateWorkspace(w: BillingWorkspace): BillingWorkspace =
    workbenchCall("/workspaces/${w.id}/generate", "POST", workbenchJson.encodeToString(RevisionRequest(w.revision)))

internal suspend fun saveWorkspace(w: BillingWorkspace): BillingWorkspace =
    workbenchCall("/workspaces/${w.id}", "PUT", workbenchJson.encodeToString(WorkspaceEdit(w.revision, w.invoice, w.entries, w.notes, w.expenses)))

internal suspend fun redraftWorkspaceEntry(
    w: BillingWorkspace,
    sourceId: String,
    hint: String,
): BillingWorkspace = workbenchCall("/workspaces/${w.id}/redraft/$sourceId", "POST", workbenchJson.encodeToString(RedraftRequest(w.revision, hint)))

internal suspend fun approveAllWorkspaceEntries(w: BillingWorkspace): BulkApproveResult =
    workbenchCall("/workspaces/${w.id}/approve-all", "POST", workbenchJson.encodeToString(BulkApproveRequest(w.revision)))

internal suspend fun saveEntryAsExample(
    w: BillingWorkspace,
    sourceId: String,
): BillingExample = workbenchCall("/workspaces/${w.id}/examples/$sourceId", "POST", workbenchJson.encodeToString(RevisionRequest(w.revision)))

internal suspend fun getWorkspaceHistory(id: String): List<BillingAudit> = workbenchCall("/workspaces/$id/history")

internal suspend fun getProfileExamples(profileId: String): List<BillingExample> = workbenchCall("/profiles/$profileId/examples")

internal suspend fun deleteProfileExample(
    profileId: String,
    exampleId: String,
) {
    workbenchCall<JsonObject>("/profiles/$profileId/examples/$exampleId", "DELETE", "{}")
}

internal suspend fun getPreviousBills(profileId: String): List<PreviousBill> = workbenchCall("/profiles/$profileId/previous-bills")

internal suspend fun uploadPreviousBill(
    profileId: String,
    upload: PreviousBillUpload,
): PreviousBill = workbenchCall("/profiles/$profileId/previous-bills", "POST", workbenchJson.encodeToString(upload))

internal suspend fun deletePreviousBill(
    profileId: String,
    billId: String,
) {
    workbenchCall<JsonObject>("/profiles/$profileId/previous-bills/$billId", "DELETE", "{}")
}

internal suspend fun saveBillingClient(client: BillingClient): BillingClient = workbenchCall("/clients", "POST", workbenchJson.encodeToString(client))

internal suspend fun deleteBillingClient(id: String) {
    workbenchCall<JsonObject>("/clients/$id", "DELETE", "{}")
}

internal suspend fun importNoteFile(request: NoteImportRequest): NoteImportResult =
    workbenchCall("/import", "POST", workbenchJson.encodeToString(request))

internal suspend fun approveWorkspaceEntry(
    w: BillingWorkspace,
    sourceId: String,
): BillingWorkspace =
    workbenchCall("/workspaces/${w.id}/approve/$sourceId", "POST", workbenchJson.encodeToString(RevisionRequest(w.revision)))

internal suspend fun exportWorkspace(
    w: BillingWorkspace,
    format: String,
): BillingDownload = workbenchCall("/workspaces/${w.id}/export/$format", "POST", workbenchJson.encodeToString(RevisionRequest(w.revision)))

internal suspend fun deleteWorkspace(w: BillingWorkspace) {
    workbenchCall<JsonObject>("/workspaces/${w.id}", "DELETE", workbenchJson.encodeToString(RevisionRequest(w.revision)))
}

internal suspend fun deleteBillingProfile(id: String) {
    workbenchCall<JsonObject>("/profiles/$id", "DELETE", "{}")
}

internal suspend fun subscriptionLink(action: String): BillingLink = workbenchCall("/subscription/$action", "POST", "{}")

internal suspend fun refreshBillingAccess(): BillingAccess = workbenchCall("/subscription/refresh", "POST", "{}")

internal fun downloadBillingFile(file: BillingDownload) {
    val blob = Blob(arrayOf(file.content), BlobPropertyBag(type = file.contentType))
    val url = js("URL.createObjectURL(blob)").unsafeCast<String>()
    val anchor = document.createElement("a") as HTMLAnchorElement
    anchor.href = url
    anchor.download = file.filename
    document.body?.appendChild(anchor)
    anchor.click()
    anchor.remove()
    window.setTimeout({ js("URL.revokeObjectURL(url)") }, 1000)
}

internal fun newBillingRequestId(): String = js("crypto.randomUUID()").unsafeCast<String>()

/** Pasted notes for a new workspace. Unreadable dates or times are reported instead of silently dropped. */
internal fun parseBillingNotes(
    text: String,
    month: String = "",
): List<SourceNote> {
    val result = NoteParsing.parseText(text, month)
    require(result.notes.isNotEmpty()) { "Add at least one rough note." }
    require(result.warnings.isEmpty()) { result.warnings.joinToString(" ") }
    require(result.notes.size <= NoteParsing.MAX_NOTES) { "Add 1–100 notes per workspace." }
    return result.notes
}
